/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { apiClient } from '../../actions/api';
import { Scorecard, ScorecardGoal } from '../../types/scorecard';
import { HistoricalReportData, HistoricalReportDataByPeriod, ReportData, ReportOptions } from '../../types/reportData';
import { UtrvStatus } from '../../constants/status';
import { DownloadMultiScope } from '../../types/download';
import { SurveyModelMinimalUtrv } from '../../model/surveyData';

export interface SDGReportGoal {
  goal: string,
  metricsIncluded: ReportData[],
  contribution: ScorecardGoal,
  standardsOrFrameworks: string[],
  statusSummary: {
    total: number,
    completed: number,
    verified: number,
    evidenced: number,
    na: number,
    nr: number,
  }
}

export interface SDGReport {
  materiality: {
    [key: number]: number
  },
  contribution: Scorecard,
  goals: SDGReportGoal[]
}

// @TODO deprecated in favour of POST /:id/report
export const reportData = async (
  surveyId: string,
  type: string,
  statuses: UtrvStatus[] = [],
  options: ReportOptions = {}
): Promise<ReportData[]> => {
  return apiClient
    .get(`surveys/${surveyId}/report/${type}`, {
      params: { statuses, ...options },
    })
    .then((response) => response.data.data);
};

export const reportMultiScopeData = async (
  surveyId: string,
  downloadScope: DownloadMultiScope
): Promise<ReportData[]> => {
  return apiClient.post(`surveys/${surveyId}/report`, downloadScope).then((response) => response.data.data);
};

export const reportDataWithHistoryScope = async (surveyId: string, downloadScope: DownloadMultiScope): Promise<{ historical: HistoricalReportData[], targets: SurveyModelMinimalUtrv[]  }> => {
  return apiClient
    .post(`surveys/${surveyId}/report/historical`, downloadScope)
    .then((response) => response.data.data);
};

export const reportDataWithHistoryByInitiativeId = async (initiativeId: string, type: string): Promise<HistoricalReportDataByPeriod> => {
  return apiClient.get(`initiatives/${initiativeId}/report/${type}/historical/?limit=5`)
    .then(response => response.data.data)
}

export const reportDataWithHistoryByInitiativeIdBySurveyId = async (initiativeId: string, type: string, surveyId: string): Promise<HistoricalReportData> => {
  return apiClient.get(`initiatives/${initiativeId}/report/${type}/historical/survey/${surveyId}`)
    .then(response => response.data.data)
}

export const portfolioTrackerReportDataWithHistoryByInitiativeId = async (portfolioId: string, initiativeId: string, type: string): Promise<HistoricalReportDataByPeriod> => {
  return apiClient.get(`portfolios/${portfolioId}/initiatives/${initiativeId}/report/${type}/historical/?limit=5`)
    .then(response => response.data.data)
}

export const portfolioTrackerReportDataWithHistoryByInitiativeIdBySurveyId = async (portfolioId: string, initiativeId: string, type: string, surveyId: string): Promise<HistoricalReportData> => {
  return apiClient.get(`portfolios/${portfolioId}/initiatives/${initiativeId}/report/${type}/historical/survey/${surveyId}`)
    .then(response => response.data.data)
}

export const sdgReportData = async (surveyId: string, type: string, options?: ReportOptions): Promise<SDGReport> => {
  return apiClient
    .get(`surveys/${surveyId}/report/${type}`, {
      params: { ...(options || {}) },
    })
    .then((response) => response.data.data);
};
